/**
 * NatStat Data Service
 * Fetches sports data from BigQuery natstat dataset across all 30 services.
 *
 * Sports: Basketball (17), Baseball (5), Hockey (5), Football (2), Soccer (1)
 *
 * BigQuery Tables:
 * - slam-orioles.natstat.dim_services (30 services across 5 sports)
 * - slam-orioles.natstat.dim_teams (2,051 teams across all leagues)
 * - slam-orioles.natstat.dim_players (2,449 players)
 * - slam-orioles.natstat.fact_games (5,900+ games)
 */

import axios from 'axios';
import { NODE_BACKEND_URL } from './constant';
import { getAuthToken } from './helpers';

// All NatStat Services by Sport
export const NATSTAT_SERVICES = {
  Basketball: [
    { code: 'NBA', name: 'National Basketball Association', level: 'pro', region: 'NA' },
    { code: 'GL', name: 'NBA G League', level: 'minor', region: 'NA' },
    { code: 'WNBA', name: "Women's NBA", level: 'pro', region: 'NA' },
    { code: 'MBB', name: "NCAA Men's Basketball D1", level: 'college_d1', region: 'NA' },
    { code: 'MB2', name: "NCAA Men's Basketball D2", level: 'college_d2', region: 'NA' },
    { code: 'MB3', name: "NCAA Men's Basketball D3", level: 'college_d3', region: 'NA' },
    { code: 'MBIA', name: "NAIA Men's Basketball", level: 'naia', region: 'NA' },
    { code: 'MBJC', name: "JuCo Men's Basketball", level: 'juco', region: 'NA' },
    { code: 'WBB', name: "NCAA Women's Basketball D1", level: 'college_d1', region: 'NA' },
    { code: 'WB2', name: "NCAA Women's Basketball D2", level: 'college_d2', region: 'NA' },
    { code: 'WB3', name: "NCAA Women's Basketball D3", level: 'college_d3', region: 'NA' },
    { code: 'WBIA', name: "NAIA Women's Basketball", level: 'naia', region: 'NA' },
    { code: 'WBJC', name: "JuCo Women's Basketball", level: 'juco', region: 'NA' },
    { code: 'WBPro', name: "Women's Pro Basketball Intl", level: 'pro', region: 'intl' },
    { code: 'EuroBB', name: 'European Basketball', level: 'pro', region: 'europe' },
    { code: 'AsiaBB', name: 'Asian Basketball', level: 'pro', region: 'asia' },
    { code: 'AmerBB', name: 'Americas Basketball', level: 'pro', region: 'americas' },
  ],
  Baseball: [
    { code: 'MLB', name: 'Major League Baseball', level: 'pro', region: 'NA' },
    { code: 'MiLB', name: 'Minor League Baseball', level: 'minor', region: 'NA' },
    { code: 'KBO', name: 'Korean Baseball Organization', level: 'pro', region: 'asia' },
    { code: 'NPB', name: 'Nippon Professional Baseball', level: 'pro', region: 'asia' },
    { code: 'CBB', name: 'College Baseball', level: 'college', region: 'NA' },
  ],
  Hockey: [
    { code: 'NHL', name: 'National Hockey League', level: 'pro', region: 'NA' },
    { code: 'MiLH', name: 'Minor League Hockey', level: 'minor', region: 'NA' },
    { code: 'MHK', name: "NCAA Men's Hockey", level: 'college', region: 'NA' },
    { code: 'KHL', name: 'Kontinental Hockey League', level: 'pro', region: 'europe' },
    { code: 'WHK', name: "NCAA Women's Hockey", level: 'college', region: 'NA' },
  ],
  Football: [
    { code: 'PFB', name: 'Pro Football (NFL)', level: 'pro', region: 'NA' },
    { code: 'CFB', name: 'College Football', level: 'college', region: 'NA' },
  ],
  Soccer: [
    { code: 'FC', name: 'Soccer/Football Club', level: 'pro', region: 'intl' },
  ],
  Golf: [
    { code: 'PGA', name: 'PGA Tour', level: 'pro', region: 'NA' },
    { code: 'LPGA', name: 'LPGA Tour', level: 'pro', region: 'NA' },
    { code: 'CGF', name: 'College Golf', level: 'college', region: 'NA' },
  ],
};

// Helper to build compact team entries
const t = (id, name, full, conf) => ({ team_id: id, team_name: name, full_name: full, conference: conf });

// Fallback teams for key leagues when backend API is unavailable
// This ensures the TeamSelector works even when Cloud Run returns 502
export const FALLBACK_TEAMS = {
  // ========== NCAA Men's Basketball D1 (MBB) ==========
  MBB: [
    // ACC
    t('boston-college', 'Boston College', 'Boston College Eagles', 'ACC'),
    t('california', 'California', 'California Golden Bears', 'ACC'),
    t('clemson', 'Clemson', 'Clemson Tigers', 'ACC'),
    t('duke', 'Duke', 'Duke Blue Devils', 'ACC'),
    t('florida-state', 'Florida State', 'Florida State Seminoles', 'ACC'),
    t('georgia-tech', 'Georgia Tech', 'Georgia Tech Yellow Jackets', 'ACC'),
    t('louisville', 'Louisville', 'Louisville Cardinals', 'ACC'),
    t('miami-fl', 'Miami (FL)', 'Miami Hurricanes', 'ACC'),
    t('nc-state', 'NC State', 'NC State Wolfpack', 'ACC'),
    t('north-carolina', 'North Carolina', 'North Carolina Tar Heels', 'ACC'),
    t('notre-dame', 'Notre Dame', 'Notre Dame Fighting Irish', 'ACC'),
    t('pittsburgh', 'Pittsburgh', 'Pittsburgh Panthers', 'ACC'),
    t('smu', 'SMU', 'SMU Mustangs', 'ACC'),
    t('stanford', 'Stanford', 'Stanford Cardinal', 'ACC'),
    t('syracuse', 'Syracuse', 'Syracuse Orange', 'ACC'),
    t('virginia', 'Virginia', 'Virginia Cavaliers', 'ACC'),
    t('virginia-tech', 'Virginia Tech', 'Virginia Tech Hokies', 'ACC'),
    t('wake-forest', 'Wake Forest', 'Wake Forest Demon Deacons', 'ACC'),
    // Big 12
    t('arizona', 'Arizona', 'Arizona Wildcats', 'Big 12'),
    t('arizona-state', 'Arizona State', 'Arizona State Sun Devils', 'Big 12'),
    t('baylor', 'Baylor', 'Baylor Bears', 'Big 12'),
    t('byu', 'BYU', 'BYU Cougars', 'Big 12'),
    t('cincinnati', 'Cincinnati', 'Cincinnati Bearcats', 'Big 12'),
    t('colorado', 'Colorado', 'Colorado Buffaloes', 'Big 12'),
    t('houston', 'Houston', 'Houston Cougars', 'Big 12'),
    t('iowa-state', 'Iowa State', 'Iowa State Cyclones', 'Big 12'),
    t('kansas', 'Kansas', 'Kansas Jayhawks', 'Big 12'),
    t('kansas-state', 'Kansas State', 'Kansas State Wildcats', 'Big 12'),
    t('oklahoma-state', 'Oklahoma State', 'Oklahoma State Cowboys', 'Big 12'),
    t('tcu', 'TCU', 'TCU Horned Frogs', 'Big 12'),
    t('texas-tech', 'Texas Tech', 'Texas Tech Red Raiders', 'Big 12'),
    t('ucf', 'UCF', 'UCF Knights', 'Big 12'),
    t('utah', 'Utah', 'Utah Utes', 'Big 12'),
    t('west-virginia', 'West Virginia', 'West Virginia Mountaineers', 'Big 12'),
    // Big East
    t('butler', 'Butler', 'Butler Bulldogs', 'Big East'),
    t('connecticut', 'Connecticut', 'Connecticut Huskies', 'Big East'),
    t('creighton', 'Creighton', 'Creighton Bluejays', 'Big East'),
    t('depaul', 'DePaul', 'DePaul Blue Demons', 'Big East'),
    t('georgetown', 'Georgetown', 'Georgetown Hoyas', 'Big East'),
    t('marquette', 'Marquette', 'Marquette Golden Eagles', 'Big East'),
    t('providence', 'Providence', 'Providence Friars', 'Big East'),
    t('seton-hall', 'Seton Hall', 'Seton Hall Pirates', 'Big East'),
    t('st-johns', "St. John's", "St. John's Red Storm", 'Big East'),
    t('villanova', 'Villanova', 'Villanova Wildcats', 'Big East'),
    t('xavier', 'Xavier', 'Xavier Musketeers', 'Big East'),
    // Big Ten
    t('illinois', 'Illinois', 'Illinois Fighting Illini', 'Big Ten'),
    t('indiana', 'Indiana', 'Indiana Hoosiers', 'Big Ten'),
    t('iowa', 'Iowa', 'Iowa Hawkeyes', 'Big Ten'),
    t('maryland', 'Maryland', 'Maryland Terrapins', 'Big Ten'),
    t('michigan', 'Michigan', 'Michigan Wolverines', 'Big Ten'),
    t('michigan-state', 'Michigan State', 'Michigan State Spartans', 'Big Ten'),
    t('minnesota', 'Minnesota', 'Minnesota Golden Gophers', 'Big Ten'),
    t('nebraska', 'Nebraska', 'Nebraska Cornhuskers', 'Big Ten'),
    t('northwestern', 'Northwestern', 'Northwestern Wildcats', 'Big Ten'),
    t('ohio-state', 'Ohio State', 'Ohio State Buckeyes', 'Big Ten'),
    t('oregon', 'Oregon', 'Oregon Ducks', 'Big Ten'),
    t('penn-state', 'Penn State', 'Penn State Nittany Lions', 'Big Ten'),
    t('purdue', 'Purdue', 'Purdue Boilermakers', 'Big Ten'),
    t('rutgers', 'Rutgers', 'Rutgers Scarlet Knights', 'Big Ten'),
    t('ucla', 'UCLA', 'UCLA Bruins', 'Big Ten'),
    t('usc', 'USC', 'USC Trojans', 'Big Ten'),
    t('washington', 'Washington', 'Washington Huskies', 'Big Ten'),
    t('wisconsin', 'Wisconsin', 'Wisconsin Badgers', 'Big Ten'),
    // SEC
    t('alabama', 'Alabama', 'Alabama Crimson Tide', 'SEC'),
    t('arkansas', 'Arkansas', 'Arkansas Razorbacks', 'SEC'),
    t('auburn', 'Auburn', 'Auburn Tigers', 'SEC'),
    t('florida', 'Florida', 'Florida Gators', 'SEC'),
    t('georgia', 'Georgia', 'Georgia Bulldogs', 'SEC'),
    t('kentucky', 'Kentucky', 'Kentucky Wildcats', 'SEC'),
    t('lsu', 'LSU', 'LSU Tigers', 'SEC'),
    t('mississippi-state', 'Mississippi State', 'Mississippi State Bulldogs', 'SEC'),
    t('missouri', 'Missouri', 'Missouri Tigers', 'SEC'),
    t('oklahoma', 'Oklahoma', 'Oklahoma Sooners', 'SEC'),
    t('ole-miss', 'Ole Miss', 'Ole Miss Rebels', 'SEC'),
    t('south-carolina', 'South Carolina', 'South Carolina Gamecocks', 'SEC'),
    t('tennessee', 'Tennessee', 'Tennessee Volunteers', 'SEC'),
    t('texas', 'Texas', 'Texas Longhorns', 'SEC'),
    t('texas-am', 'Texas A&M', 'Texas A&M Aggies', 'SEC'),
    t('vanderbilt', 'Vanderbilt', 'Vanderbilt Commodores', 'SEC'),
    // American Athletic
    t('charlotte', 'Charlotte', 'Charlotte 49ers', 'American'),
    t('east-carolina', 'East Carolina', 'East Carolina Pirates', 'American'),
    t('fau', 'FAU', 'FAU Owls', 'American'),
    t('memphis', 'Memphis', 'Memphis Tigers', 'American'),
    t('navy', 'Navy', 'Navy Midshipmen', 'American'),
    t('north-texas', 'North Texas', 'North Texas Mean Green', 'American'),
    t('rice', 'Rice', 'Rice Owls', 'American'),
    t('south-florida', 'South Florida', 'South Florida Bulls', 'American'),
    t('temple', 'Temple', 'Temple Owls', 'American'),
    t('tulane', 'Tulane', 'Tulane Green Wave', 'American'),
    t('tulsa', 'Tulsa', 'Tulsa Golden Hurricane', 'American'),
    t('uab', 'UAB', 'UAB Blazers', 'American'),
    t('utsa', 'UTSA', 'UTSA Roadrunners', 'American'),
    t('wichita-state', 'Wichita State', 'Wichita State Shockers', 'American'),
    // Atlantic 10
    t('davidson', 'Davidson', 'Davidson Wildcats', 'Atlantic 10'),
    t('dayton', 'Dayton', 'Dayton Flyers', 'Atlantic 10'),
    t('duquesne', 'Duquesne', 'Duquesne Dukes', 'Atlantic 10'),
    t('fordham', 'Fordham', 'Fordham Rams', 'Atlantic 10'),
    t('george-mason', 'George Mason', 'George Mason Patriots', 'Atlantic 10'),
    t('george-washington', 'George Washington', 'George Washington Colonials', 'Atlantic 10'),
    t('la-salle', 'La Salle', 'La Salle Explorers', 'Atlantic 10'),
    t('loyola-chicago', 'Loyola Chicago', 'Loyola Chicago Ramblers', 'Atlantic 10'),
    t('massachusetts', 'UMass', 'UMass Minutemen', 'Atlantic 10'),
    t('rhode-island', 'Rhode Island', 'Rhode Island Rams', 'Atlantic 10'),
    t('richmond', 'Richmond', 'Richmond Spiders', 'Atlantic 10'),
    t('saint-josephs', "Saint Joseph's", "Saint Joseph's Hawks", 'Atlantic 10'),
    t('saint-louis', 'Saint Louis', 'Saint Louis Billikens', 'Atlantic 10'),
    t('st-bonaventure', 'St. Bonaventure', 'St. Bonaventure Bonnies', 'Atlantic 10'),
    t('vcu', 'VCU', 'VCU Rams', 'Atlantic 10'),
    // CAA
    t('campbell', 'Campbell', 'Campbell Fighting Camels', 'CAA'),
    t('charleston', 'Charleston', 'Charleston Cougars', 'CAA'),
    t('delaware', 'Delaware', 'Delaware Fightin Blue Hens', 'CAA'),
    t('drexel', 'Drexel', 'Drexel Dragons', 'CAA'),
    t('elon', 'Elon', 'Elon Phoenix', 'CAA'),
    t('hampton', 'Hampton', 'Hampton Pirates', 'CAA'),
    t('hofstra', 'Hofstra', 'Hofstra Pride', 'CAA'),
    t('monmouth', 'Monmouth', 'Monmouth Hawks', 'CAA'),
    t('northeastern', 'Northeastern', 'Northeastern Huskies', 'CAA'),
    t('stony-brook', 'Stony Brook', 'Stony Brook Seawolves', 'CAA'),
    t('towson', 'Towson', 'Towson Tigers', 'CAA'),
    t('unc-wilmington', 'UNC Wilmington', 'UNC Wilmington Seahawks', 'CAA'),
    t('william-mary', 'William & Mary', 'William & Mary Tribe', 'CAA'),
    // Conference USA
    t('fiu', 'FIU', 'FIU Panthers', 'Conference USA'),
    t('jacksonville-state', 'Jacksonville State', 'Jacksonville State Gamecocks', 'Conference USA'),
    t('kennesaw-state', 'Kennesaw State', 'Kennesaw State Owls', 'Conference USA'),
    t('liberty', 'Liberty', 'Liberty Flames', 'Conference USA'),
    t('louisiana-tech', 'Louisiana Tech', 'Louisiana Tech Bulldogs', 'Conference USA'),
    t('middle-tennessee', 'Middle Tennessee', 'Middle Tennessee Blue Raiders', 'Conference USA'),
    t('new-mexico-state', 'New Mexico State', 'New Mexico State Aggies', 'Conference USA'),
    t('sam-houston', 'Sam Houston', 'Sam Houston Bearkats', 'Conference USA'),
    t('western-kentucky', 'Western Kentucky', 'Western Kentucky Hilltoppers', 'Conference USA'),
    // Horizon League
    t('cleveland-state', 'Cleveland State', 'Cleveland State Vikings', 'Horizon'),
    t('detroit-mercy', 'Detroit Mercy', 'Detroit Mercy Titans', 'Horizon'),
    t('green-bay', 'Green Bay', 'Green Bay Phoenix', 'Horizon'),
    t('iupui', 'IUPUI', 'IUPUI Jaguars', 'Horizon'),
    t('milwaukee', 'Milwaukee', 'Milwaukee Panthers', 'Horizon'),
    t('northern-kentucky', 'Northern Kentucky', 'Northern Kentucky Norse', 'Horizon'),
    t('oakland', 'Oakland', 'Oakland Golden Grizzlies', 'Horizon'),
    t('purdue-fw', 'Purdue Fort Wayne', 'Purdue Fort Wayne Mastodons', 'Horizon'),
    t('robert-morris', 'Robert Morris', 'Robert Morris Colonials', 'Horizon'),
    t('wright-state', 'Wright State', 'Wright State Raiders', 'Horizon'),
    t('youngstown-state', 'Youngstown State', 'Youngstown State Penguins', 'Horizon'),
    // Ivy League
    t('brown', 'Brown', 'Brown Bears', 'Ivy League'),
    t('columbia', 'Columbia', 'Columbia Lions', 'Ivy League'),
    t('cornell', 'Cornell', 'Cornell Big Red', 'Ivy League'),
    t('dartmouth', 'Dartmouth', 'Dartmouth Big Green', 'Ivy League'),
    t('harvard', 'Harvard', 'Harvard Crimson', 'Ivy League'),
    t('penn', 'Penn', 'Penn Quakers', 'Ivy League'),
    t('princeton', 'Princeton', 'Princeton Tigers', 'Ivy League'),
    t('yale', 'Yale', 'Yale Bulldogs', 'Ivy League'),
    // MAAC
    t('canisius', 'Canisius', 'Canisius Golden Griffins', 'MAAC'),
    t('fairfield', 'Fairfield', 'Fairfield Stags', 'MAAC'),
    t('iona', 'Iona', 'Iona Gaels', 'MAAC'),
    t('manhattan', 'Manhattan', 'Manhattan Jaspers', 'MAAC'),
    t('marist', 'Marist', 'Marist Red Foxes', 'MAAC'),
    t('niagara', 'Niagara', 'Niagara Purple Eagles', 'MAAC'),
    t('quinnipiac', 'Quinnipiac', 'Quinnipiac Bobcats', 'MAAC'),
    t('rider', 'Rider', 'Rider Broncs', 'MAAC'),
    t('saint-peters', "Saint Peter's", "Saint Peter's Peacocks", 'MAAC'),
    t('siena', 'Siena', 'Siena Saints', 'MAAC'),
    // MAC
    t('akron', 'Akron', 'Akron Zips', 'MAC'),
    t('ball-state', 'Ball State', 'Ball State Cardinals', 'MAC'),
    t('bowling-green', 'Bowling Green', 'Bowling Green Falcons', 'MAC'),
    t('buffalo', 'Buffalo', 'Buffalo Bulls', 'MAC'),
    t('central-michigan', 'Central Michigan', 'Central Michigan Chippewas', 'MAC'),
    t('eastern-michigan', 'Eastern Michigan', 'Eastern Michigan Eagles', 'MAC'),
    t('kent-state', 'Kent State', 'Kent State Golden Flashes', 'MAC'),
    t('miami-oh', 'Miami (OH)', 'Miami (OH) RedHawks', 'MAC'),
    t('northern-illinois', 'Northern Illinois', 'Northern Illinois Huskies', 'MAC'),
    t('ohio', 'Ohio', 'Ohio Bobcats', 'MAC'),
    t('toledo', 'Toledo', 'Toledo Rockets', 'MAC'),
    t('western-michigan', 'Western Michigan', 'Western Michigan Broncos', 'MAC'),
    // Missouri Valley
    t('belmont', 'Belmont', 'Belmont Bruins', 'Missouri Valley'),
    t('bradley', 'Bradley', 'Bradley Braves', 'Missouri Valley'),
    t('drake', 'Drake', 'Drake Bulldogs', 'Missouri Valley'),
    t('evansville', 'Evansville', 'Evansville Purple Aces', 'Missouri Valley'),
    t('illinois-state', 'Illinois State', 'Illinois State Redbirds', 'Missouri Valley'),
    t('indiana-state', 'Indiana State', 'Indiana State Sycamores', 'Missouri Valley'),
    t('missouri-state', 'Missouri State', 'Missouri State Bears', 'Missouri Valley'),
    t('murray-state', 'Murray State', 'Murray State Racers', 'Missouri Valley'),
    t('northern-iowa', 'Northern Iowa', 'Northern Iowa Panthers', 'Missouri Valley'),
    t('southern-illinois', 'Southern Illinois', 'Southern Illinois Salukis', 'Missouri Valley'),
    t('uic', 'UIC', 'UIC Flames', 'Missouri Valley'),
    t('valparaiso', 'Valparaiso', 'Valparaiso Beacons', 'Missouri Valley'),
    // Mountain West
    t('air-force', 'Air Force', 'Air Force Falcons', 'Mountain West'),
    t('boise-state', 'Boise State', 'Boise State Broncos', 'Mountain West'),
    t('colorado-state', 'Colorado State', 'Colorado State Rams', 'Mountain West'),
    t('fresno-state', 'Fresno State', 'Fresno State Bulldogs', 'Mountain West'),
    t('nevada', 'Nevada', 'Nevada Wolf Pack', 'Mountain West'),
    t('new-mexico', 'New Mexico', 'New Mexico Lobos', 'Mountain West'),
    t('san-diego-state', 'San Diego State', 'San Diego State Aztecs', 'Mountain West'),
    t('san-jose-state', 'San Jose State', 'San Jose State Spartans', 'Mountain West'),
    t('unlv', 'UNLV', 'UNLV Rebels', 'Mountain West'),
    t('utah-state', 'Utah State', 'Utah State Aggies', 'Mountain West'),
    t('wyoming', 'Wyoming', 'Wyoming Cowboys', 'Mountain West'),
    // Patriot League
    t('american', 'American', 'American Eagles', 'Patriot League'),
    t('army', 'Army', 'Army Black Knights', 'Patriot League'),
    t('boston-university', 'Boston University', 'Boston University Terriers', 'Patriot League'),
    t('bucknell', 'Bucknell', 'Bucknell Bison', 'Patriot League'),
    t('colgate', 'Colgate', 'Colgate Raiders', 'Patriot League'),
    t('holy-cross', 'Holy Cross', 'Holy Cross Crusaders', 'Patriot League'),
    t('lafayette', 'Lafayette', 'Lafayette Leopards', 'Patriot League'),
    t('lehigh', 'Lehigh', 'Lehigh Mountain Hawks', 'Patriot League'),
    t('loyola-md', 'Loyola Maryland', 'Loyola Maryland Greyhounds', 'Patriot League'),
    t('navy-bb', 'Navy', 'Navy Midshipmen', 'Patriot League'),
    // Southern Conference
    t('chattanooga', 'Chattanooga', 'Chattanooga Mocs', 'Southern'),
    t('citadel', 'The Citadel', 'The Citadel Bulldogs', 'Southern'),
    t('east-tennessee-state', 'ETSU', 'ETSU Buccaneers', 'Southern'),
    t('furman', 'Furman', 'Furman Paladins', 'Southern'),
    t('mercer', 'Mercer', 'Mercer Bears', 'Southern'),
    t('samford', 'Samford', 'Samford Bulldogs', 'Southern'),
    t('unc-greensboro', 'UNC Greensboro', 'UNC Greensboro Spartans', 'Southern'),
    t('vmi', 'VMI', 'VMI Keydets', 'Southern'),
    t('western-carolina', 'Western Carolina', 'Western Carolina Catamounts', 'Southern'),
    t('wofford', 'Wofford', 'Wofford Terriers', 'Southern'),
    // Sun Belt
    t('app-state', 'Appalachian State', 'Appalachian State Mountaineers', 'Sun Belt'),
    t('arkansas-state', 'Arkansas State', 'Arkansas State Red Wolves', 'Sun Belt'),
    t('coastal-carolina', 'Coastal Carolina', 'Coastal Carolina Chanticleers', 'Sun Belt'),
    t('georgia-southern', 'Georgia Southern', 'Georgia Southern Eagles', 'Sun Belt'),
    t('georgia-state', 'Georgia State', 'Georgia State Panthers', 'Sun Belt'),
    t('james-madison', 'James Madison', 'James Madison Dukes', 'Sun Belt'),
    t('louisiana', 'Louisiana', 'Louisiana Ragin Cajuns', 'Sun Belt'),
    t('louisiana-monroe', 'UL Monroe', 'UL Monroe Warhawks', 'Sun Belt'),
    t('marshall', 'Marshall', 'Marshall Thundering Herd', 'Sun Belt'),
    t('old-dominion', 'Old Dominion', 'Old Dominion Monarchs', 'Sun Belt'),
    t('south-alabama', 'South Alabama', 'South Alabama Jaguars', 'Sun Belt'),
    t('southern-miss', 'Southern Miss', 'Southern Miss Golden Eagles', 'Sun Belt'),
    t('texas-state', 'Texas State', 'Texas State Bobcats', 'Sun Belt'),
    t('troy', 'Troy', 'Troy Trojans', 'Sun Belt'),
    // WCC
    t('gonzaga', 'Gonzaga', 'Gonzaga Bulldogs', 'WCC'),
    t('lmu', 'LMU', 'LMU Lions', 'WCC'),
    t('pacific', 'Pacific', 'Pacific Tigers', 'WCC'),
    t('pepperdine', 'Pepperdine', 'Pepperdine Waves', 'WCC'),
    t('portland', 'Portland', 'Portland Pilots', 'WCC'),
    t('saint-marys', "Saint Mary's", "Saint Mary's Gaels", 'WCC'),
    t('san-diego', 'San Diego', 'San Diego Toreros', 'WCC'),
    t('san-francisco', 'San Francisco', 'San Francisco Dons', 'WCC'),
    t('santa-clara', 'Santa Clara', 'Santa Clara Broncos', 'WCC'),
    // ASUN
    t('bellarmine', 'Bellarmine', 'Bellarmine Knights', 'ASUN'),
    t('central-arkansas', 'Central Arkansas', 'Central Arkansas Bears', 'ASUN'),
    t('eastern-kentucky', 'Eastern Kentucky', 'Eastern Kentucky Colonels', 'ASUN'),
    t('florida-gulf-coast', 'FGCU', 'FGCU Eagles', 'ASUN'),
    t('jacksonville', 'Jacksonville', 'Jacksonville Dolphins', 'ASUN'),
    t('lipscomb', 'Lipscomb', 'Lipscomb Bisons', 'ASUN'),
    t('north-alabama', 'North Alabama', 'North Alabama Lions', 'ASUN'),
    t('north-florida', 'North Florida', 'North Florida Ospreys', 'ASUN'),
    t('queens', 'Queens', 'Queens Royals', 'ASUN'),
    t('stetson', 'Stetson', 'Stetson Hatters', 'ASUN'),
    // America East
    t('albany', 'Albany', 'Albany Great Danes', 'America East'),
    t('binghamton', 'Binghamton', 'Binghamton Bearcats', 'America East'),
    t('bryant', 'Bryant', 'Bryant Bulldogs', 'America East'),
    t('maine', 'Maine', 'Maine Black Bears', 'America East'),
    t('new-hampshire', 'New Hampshire', 'New Hampshire Wildcats', 'America East'),
    t('njit', 'NJIT', 'NJIT Highlanders', 'America East'),
    t('umbc', 'UMBC', 'UMBC Retrievers', 'America East'),
    t('uvm', 'Vermont', 'Vermont Catamounts', 'America East'),
    // Big Sky
    t('eastern-washington', 'Eastern Washington', 'Eastern Washington Eagles', 'Big Sky'),
    t('idaho', 'Idaho', 'Idaho Vandals', 'Big Sky'),
    t('idaho-state', 'Idaho State', 'Idaho State Bengals', 'Big Sky'),
    t('montana', 'Montana', 'Montana Grizzlies', 'Big Sky'),
    t('montana-state', 'Montana State', 'Montana State Bobcats', 'Big Sky'),
    t('northern-arizona', 'Northern Arizona', 'Northern Arizona Lumberjacks', 'Big Sky'),
    t('northern-colorado', 'Northern Colorado', 'Northern Colorado Bears', 'Big Sky'),
    t('portland-state', 'Portland State', 'Portland State Vikings', 'Big Sky'),
    t('sacramento-state', 'Sacramento State', 'Sacramento State Hornets', 'Big Sky'),
    t('weber-state', 'Weber State', 'Weber State Wildcats', 'Big Sky'),
    // Big West
    t('cal-poly', 'Cal Poly', 'Cal Poly Mustangs', 'Big West'),
    t('cal-state-bakersfield', 'CSU Bakersfield', 'CSU Bakersfield Roadrunners', 'Big West'),
    t('cal-state-fullerton', 'CSU Fullerton', 'CSU Fullerton Titans', 'Big West'),
    t('cal-state-northridge', 'CSU Northridge', 'CSU Northridge Matadors', 'Big West'),
    t('hawaii', 'Hawaii', 'Hawaii Rainbow Warriors', 'Big West'),
    t('long-beach-state', 'Long Beach State', 'Long Beach State Beach', 'Big West'),
    t('uc-davis', 'UC Davis', 'UC Davis Aggies', 'Big West'),
    t('uc-irvine', 'UC Irvine', 'UC Irvine Anteaters', 'Big West'),
    t('uc-riverside', 'UC Riverside', 'UC Riverside Highlanders', 'Big West'),
    t('uc-san-diego', 'UC San Diego', 'UC San Diego Tritons', 'Big West'),
    t('uc-santa-barbara', 'UC Santa Barbara', 'UC Santa Barbara Gauchos', 'Big West'),
    // WAC
    t('abilene-christian', 'Abilene Christian', 'Abilene Christian Wildcats', 'WAC'),
    t('cal-baptist', 'Cal Baptist', 'Cal Baptist Lancers', 'WAC'),
    t('grand-canyon', 'Grand Canyon', 'Grand Canyon Antelopes', 'WAC'),
    t('seattle', 'Seattle', 'Seattle Redhawks', 'WAC'),
    t('stephen-f-austin', 'Stephen F. Austin', 'Stephen F. Austin Lumberjacks', 'WAC'),
    t('tarleton-state', 'Tarleton State', 'Tarleton State Texans', 'WAC'),
    t('utah-tech', 'Utah Tech', 'Utah Tech Trailblazers', 'WAC'),
    t('utah-valley', 'Utah Valley', 'Utah Valley Wolverines', 'WAC'),
    // Summit League
    t('denver', 'Denver', 'Denver Pioneers', 'Summit'),
    t('kansas-city', 'Kansas City', 'Kansas City Roos', 'Summit'),
    t('north-dakota', 'North Dakota', 'North Dakota Fighting Hawks', 'Summit'),
    t('north-dakota-state', 'North Dakota State', 'North Dakota State Bison', 'Summit'),
    t('omaha', 'Omaha', 'Omaha Mavericks', 'Summit'),
    t('oral-roberts', 'Oral Roberts', 'Oral Roberts Golden Eagles', 'Summit'),
    t('south-dakota', 'South Dakota', 'South Dakota Coyotes', 'Summit'),
    t('south-dakota-state', 'South Dakota State', 'South Dakota State Jackrabbits', 'Summit'),
    t('st-thomas', 'St. Thomas', 'St. Thomas Tommies', 'Summit'),
    // OVC
    t('eastern-illinois', 'Eastern Illinois', 'Eastern Illinois Panthers', 'OVC'),
    t('lindenwood', 'Lindenwood', 'Lindenwood Lions', 'OVC'),
    t('morehead-state', 'Morehead State', 'Morehead State Eagles', 'OVC'),
    t('se-missouri-state', 'SE Missouri State', 'SE Missouri State Redhawks', 'OVC'),
    t('siu-edwardsville', 'SIU Edwardsville', 'SIU Edwardsville Cougars', 'OVC'),
    t('tennessee-state', 'Tennessee State', 'Tennessee State Tigers', 'OVC'),
    t('tennessee-tech', 'Tennessee Tech', 'Tennessee Tech Golden Eagles', 'OVC'),
    t('ut-martin', 'UT Martin', 'UT Martin Skyhawks', 'OVC'),
    // NEC
    t('central-connecticut', 'Central Connecticut', 'Central Connecticut Blue Devils', 'NEC'),
    t('fairleigh-dickinson', 'Fairleigh Dickinson', 'Fairleigh Dickinson Knights', 'NEC'),
    t('le-moyne', 'Le Moyne', 'Le Moyne Dolphins', 'NEC'),
    t('long-island', 'LIU', 'LIU Sharks', 'NEC'),
    t('mercyhurst', 'Mercyhurst', 'Mercyhurst Lakers', 'NEC'),
    t('sacred-heart', 'Sacred Heart', 'Sacred Heart Pioneers', 'NEC'),
    t('st-francis-pa', 'St. Francis (PA)', 'St. Francis Red Flash', 'NEC'),
    t('wagner', 'Wagner', 'Wagner Seahawks', 'NEC'),
    // Southland
    t('houston-christian', 'Houston Christian', 'Houston Christian Huskies', 'Southland'),
    t('incarnate-word', 'Incarnate Word', 'Incarnate Word Cardinals', 'Southland'),
    t('lamar', 'Lamar', 'Lamar Cardinals', 'Southland'),
    t('mcneese', 'McNeese', 'McNeese Cowboys', 'Southland'),
    t('nicholls', 'Nicholls', 'Nicholls Colonels', 'Southland'),
    t('northwestern-state', 'Northwestern State', 'Northwestern State Demons', 'Southland'),
    t('southeastern-louisiana', 'SE Louisiana', 'SE Louisiana Lions', 'Southland'),
    t('texas-am-cc', 'Texas A&M-CC', 'Texas A&M-CC Islanders', 'Southland'),
    // SWAC
    t('alabama-am', 'Alabama A&M', 'Alabama A&M Bulldogs', 'SWAC'),
    t('alabama-state', 'Alabama State', 'Alabama State Hornets', 'SWAC'),
    t('alcorn-state', 'Alcorn State', 'Alcorn State Braves', 'SWAC'),
    t('bethune-cookman', 'Bethune-Cookman', 'Bethune-Cookman Wildcats', 'SWAC'),
    t('florida-am', 'Florida A&M', 'Florida A&M Rattlers', 'SWAC'),
    t('grambling', 'Grambling', 'Grambling Tigers', 'SWAC'),
    t('jackson-state', 'Jackson State', 'Jackson State Tigers', 'SWAC'),
    t('mississippi-valley', 'Mississippi Valley State', 'Mississippi Valley State Delta Devils', 'SWAC'),
    t('prairie-view', 'Prairie View A&M', 'Prairie View A&M Panthers', 'SWAC'),
    t('southern', 'Southern', 'Southern Jaguars', 'SWAC'),
    t('texas-southern', 'Texas Southern', 'Texas Southern Tigers', 'SWAC'),
    // MEAC
    t('coppin-state', 'Coppin State', 'Coppin State Eagles', 'MEAC'),
    t('delaware-state', 'Delaware State', 'Delaware State Hornets', 'MEAC'),
    t('howard', 'Howard', 'Howard Bison', 'MEAC'),
    t('maryland-eastern-shore', 'Maryland Eastern Shore', 'Maryland Eastern Shore Hawks', 'MEAC'),
    t('morgan-state', 'Morgan State', 'Morgan State Bears', 'MEAC'),
    t('norfolk-state', 'Norfolk State', 'Norfolk State Spartans', 'MEAC'),
    t('north-carolina-central', 'NC Central', 'NC Central Eagles', 'MEAC'),
    t('south-carolina-state', 'South Carolina State', 'South Carolina State Bulldogs', 'MEAC'),
  ],

  // ========== NCAA Women's Basketball D1 (WBB) — Power conferences ==========
  WBB: [
    // ACC
    t('wbb-duke', 'Duke', 'Duke Blue Devils', 'ACC'),
    t('wbb-louisville', 'Louisville', 'Louisville Cardinals', 'ACC'),
    t('wbb-nc-state', 'NC State', 'NC State Wolfpack', 'ACC'),
    t('wbb-notre-dame', 'Notre Dame', 'Notre Dame Fighting Irish', 'ACC'),
    t('wbb-north-carolina', 'North Carolina', 'North Carolina Tar Heels', 'ACC'),
    t('wbb-virginia-tech', 'Virginia Tech', 'Virginia Tech Hokies', 'ACC'),
    t('wbb-stanford', 'Stanford', 'Stanford Cardinal', 'ACC'),
    // Big 12
    t('wbb-baylor', 'Baylor', 'Baylor Bears', 'Big 12'),
    t('wbb-iowa-state', 'Iowa State', 'Iowa State Cyclones', 'Big 12'),
    t('wbb-kansas-state', 'Kansas State', 'Kansas State Wildcats', 'Big 12'),
    t('wbb-texas-tech', 'Texas Tech', 'Texas Tech Red Raiders', 'Big 12'),
    t('wbb-ucf', 'UCF', 'UCF Knights', 'Big 12'),
    // Big East
    t('wbb-connecticut', 'Connecticut', 'Connecticut Huskies', 'Big East'),
    t('wbb-creighton', 'Creighton', 'Creighton Bluejays', 'Big East'),
    t('wbb-villanova', 'Villanova', 'Villanova Wildcats', 'Big East'),
    // Big Ten
    t('wbb-indiana', 'Indiana', 'Indiana Hoosiers', 'Big Ten'),
    t('wbb-iowa', 'Iowa', 'Iowa Hawkeyes', 'Big Ten'),
    t('wbb-maryland', 'Maryland', 'Maryland Terrapins', 'Big Ten'),
    t('wbb-ohio-state', 'Ohio State', 'Ohio State Buckeyes', 'Big Ten'),
    t('wbb-usc', 'USC', 'USC Trojans', 'Big Ten'),
    t('wbb-ucla', 'UCLA', 'UCLA Bruins', 'Big Ten'),
    t('wbb-oregon', 'Oregon', 'Oregon Ducks', 'Big Ten'),
    // SEC
    t('wbb-south-carolina', 'South Carolina', 'South Carolina Gamecocks', 'SEC'),
    t('wbb-lsu', 'LSU', 'LSU Tigers', 'SEC'),
    t('wbb-texas', 'Texas', 'Texas Longhorns', 'SEC'),
    t('wbb-tennessee', 'Tennessee', 'Tennessee Lady Vols', 'SEC'),
    t('wbb-ole-miss', 'Ole Miss', 'Ole Miss Rebels', 'SEC'),
    t('wbb-oklahoma', 'Oklahoma', 'Oklahoma Sooners', 'SEC'),
  ],

  // ========== NBA ==========
  NBA: [
    // Atlantic
    t('bos', 'Boston', 'Boston Celtics', 'Atlantic'),
    t('bkn', 'Brooklyn', 'Brooklyn Nets', 'Atlantic'),
    t('nyk', 'New York', 'New York Knicks', 'Atlantic'),
    t('phi', 'Philadelphia', 'Philadelphia 76ers', 'Atlantic'),
    t('tor', 'Toronto', 'Toronto Raptors', 'Atlantic'),
    // Central
    t('chi', 'Chicago', 'Chicago Bulls', 'Central'),
    t('cle', 'Cleveland', 'Cleveland Cavaliers', 'Central'),
    t('det', 'Detroit', 'Detroit Pistons', 'Central'),
    t('ind', 'Indiana', 'Indiana Pacers', 'Central'),
    t('mil', 'Milwaukee', 'Milwaukee Bucks', 'Central'),
    // Southeast
    t('atl', 'Atlanta', 'Atlanta Hawks', 'Southeast'),
    t('cha', 'Charlotte', 'Charlotte Hornets', 'Southeast'),
    t('mia', 'Miami', 'Miami Heat', 'Southeast'),
    t('orl', 'Orlando', 'Orlando Magic', 'Southeast'),
    t('was', 'Washington', 'Washington Wizards', 'Southeast'),
    // Northwest
    t('den', 'Denver', 'Denver Nuggets', 'Northwest'),
    t('min', 'Minnesota', 'Minnesota Timberwolves', 'Northwest'),
    t('okc', 'Oklahoma City', 'Oklahoma City Thunder', 'Northwest'),
    t('por', 'Portland', 'Portland Trail Blazers', 'Northwest'),
    t('uta', 'Utah', 'Utah Jazz', 'Northwest'),
    // Pacific
    t('gsw', 'Golden State', 'Golden State Warriors', 'Pacific'),
    t('lac', 'LA Clippers', 'LA Clippers', 'Pacific'),
    t('lal', 'Los Angeles', 'Los Angeles Lakers', 'Pacific'),
    t('phx', 'Phoenix', 'Phoenix Suns', 'Pacific'),
    t('sac', 'Sacramento', 'Sacramento Kings', 'Pacific'),
    // Southwest
    t('dal', 'Dallas', 'Dallas Mavericks', 'Southwest'),
    t('hou', 'Houston', 'Houston Rockets', 'Southwest'),
    t('mem', 'Memphis', 'Memphis Grizzlies', 'Southwest'),
    t('nop', 'New Orleans', 'New Orleans Pelicans', 'Southwest'),
    t('sas', 'San Antonio', 'San Antonio Spurs', 'Southwest'),
  ],

  // ========== WNBA ==========
  WNBA: [
    t('atl-dream', 'Atlanta', 'Atlanta Dream', 'Eastern'),
    t('chi-sky', 'Chicago', 'Chicago Sky', 'Eastern'),
    t('con-sun', 'Connecticut', 'Connecticut Sun', 'Eastern'),
    t('ind-fever', 'Indiana', 'Indiana Fever', 'Eastern'),
    t('ny-liberty', 'New York', 'New York Liberty', 'Eastern'),
    t('was-mystics', 'Washington', 'Washington Mystics', 'Eastern'),
    t('dal-wings', 'Dallas', 'Dallas Wings', 'Western'),
    t('gs-valkyries', 'Golden State', 'Golden State Valkyries', 'Western'),
    t('lv-aces', 'Las Vegas', 'Las Vegas Aces', 'Western'),
    t('la-sparks', 'Los Angeles', 'Los Angeles Sparks', 'Western'),
    t('min-lynx', 'Minnesota', 'Minnesota Lynx', 'Western'),
    t('phx-mercury', 'Phoenix', 'Phoenix Mercury', 'Western'),
    t('sea-storm', 'Seattle', 'Seattle Storm', 'Western'),
  ],

  // ========== MLB ==========
  MLB: [
    // AL East
    t('bal', 'Baltimore', 'Baltimore Orioles', 'AL East'),
    t('bos-mlb', 'Boston', 'Boston Red Sox', 'AL East'),
    t('nyy', 'New York', 'New York Yankees', 'AL East'),
    t('tb', 'Tampa Bay', 'Tampa Bay Rays', 'AL East'),
    t('tor-mlb', 'Toronto', 'Toronto Blue Jays', 'AL East'),
    // AL Central
    t('cws', 'Chicago', 'Chicago White Sox', 'AL Central'),
    t('cle-mlb', 'Cleveland', 'Cleveland Guardians', 'AL Central'),
    t('det-mlb', 'Detroit', 'Detroit Tigers', 'AL Central'),
    t('kc', 'Kansas City', 'Kansas City Royals', 'AL Central'),
    t('min-mlb', 'Minnesota', 'Minnesota Twins', 'AL Central'),
    // AL West
    t('hou-mlb', 'Houston', 'Houston Astros', 'AL West'),
    t('laa', 'Los Angeles', 'Los Angeles Angels', 'AL West'),
    t('oak', 'Oakland', 'Oakland Athletics', 'AL West'),
    t('sea', 'Seattle', 'Seattle Mariners', 'AL West'),
    t('tex', 'Texas', 'Texas Rangers', 'AL West'),
    // NL East
    t('atl-mlb', 'Atlanta', 'Atlanta Braves', 'NL East'),
    t('mia-mlb', 'Miami', 'Miami Marlins', 'NL East'),
    t('nym', 'New York', 'New York Mets', 'NL East'),
    t('phi-mlb', 'Philadelphia', 'Philadelphia Phillies', 'NL East'),
    t('wsh', 'Washington', 'Washington Nationals', 'NL East'),
    // NL Central
    t('chc', 'Chicago', 'Chicago Cubs', 'NL Central'),
    t('cin', 'Cincinnati', 'Cincinnati Reds', 'NL Central'),
    t('mil-mlb', 'Milwaukee', 'Milwaukee Brewers', 'NL Central'),
    t('pit', 'Pittsburgh', 'Pittsburgh Pirates', 'NL Central'),
    t('stl', 'St. Louis', 'St. Louis Cardinals', 'NL Central'),
    // NL West
    t('ari', 'Arizona', 'Arizona Diamondbacks', 'NL West'),
    t('col', 'Colorado', 'Colorado Rockies', 'NL West'),
    t('lad', 'Los Angeles', 'Los Angeles Dodgers', 'NL West'),
    t('sd', 'San Diego', 'San Diego Padres', 'NL West'),
    t('sf', 'San Francisco', 'San Francisco Giants', 'NL West'),
  ],

  // ========== College Baseball D1 (CBB) ==========
  CBB: [
    // ACC
    t('bc-cbb', 'Boston College', 'Boston College Eagles', 'ACC'),
    t('clemson-cbb', 'Clemson', 'Clemson Tigers', 'ACC'),
    t('duke-cbb', 'Duke', 'Duke Blue Devils', 'ACC'),
    t('fsu-cbb', 'Florida State', 'Florida State Seminoles', 'ACC'),
    t('gatech-cbb', 'Georgia Tech', 'Georgia Tech Yellow Jackets', 'ACC'),
    t('louisville-cbb', 'Louisville', 'Louisville Cardinals', 'ACC'),
    t('miami-cbb', 'Miami', 'Miami Hurricanes', 'ACC'),
    t('ncstate-cbb', 'NC State', 'NC State Wolfpack', 'ACC'),
    t('unc-cbb', 'North Carolina', 'North Carolina Tar Heels', 'ACC'),
    t('notre-dame-cbb', 'Notre Dame', 'Notre Dame Fighting Irish', 'ACC'),
    t('pitt-cbb', 'Pittsburgh', 'Pittsburgh Panthers', 'ACC'),
    t('uva-cbb', 'Virginia', 'Virginia Cavaliers', 'ACC'),
    t('vt-cbb', 'Virginia Tech', 'Virginia Tech Hokies', 'ACC'),
    t('wake-cbb', 'Wake Forest', 'Wake Forest Demon Deacons', 'ACC'),
    // SEC
    t('alabama-cbb', 'Alabama', 'Alabama Crimson Tide', 'SEC'),
    t('arkansas-cbb', 'Arkansas', 'Arkansas Razorbacks', 'SEC'),
    t('auburn-cbb', 'Auburn', 'Auburn Tigers', 'SEC'),
    t('florida-cbb', 'Florida', 'Florida Gators', 'SEC'),
    t('georgia-cbb', 'Georgia', 'Georgia Bulldogs', 'SEC'),
    t('kentucky-cbb', 'Kentucky', 'Kentucky Wildcats', 'SEC'),
    t('lsu-cbb', 'LSU', 'LSU Tigers', 'SEC'),
    t('miss-state-cbb', 'Mississippi State', 'Mississippi State Bulldogs', 'SEC'),
    t('missouri-cbb', 'Missouri', 'Missouri Tigers', 'SEC'),
    t('ole-miss-cbb', 'Ole Miss', 'Ole Miss Rebels', 'SEC'),
    t('south-carolina-cbb', 'South Carolina', 'South Carolina Gamecocks', 'SEC'),
    t('tennessee-cbb', 'Tennessee', 'Tennessee Volunteers', 'SEC'),
    t('texas-cbb', 'Texas', 'Texas Longhorns', 'SEC'),
    t('texas-am-cbb', 'Texas A&M', 'Texas A&M Aggies', 'SEC'),
    t('vanderbilt-cbb', 'Vanderbilt', 'Vanderbilt Commodores', 'SEC'),
    // Big 12
    t('arizona-cbb', 'Arizona', 'Arizona Wildcats', 'Big 12'),
    t('asu-cbb', 'Arizona State', 'Arizona State Sun Devils', 'Big 12'),
    t('baylor-cbb', 'Baylor', 'Baylor Bears', 'Big 12'),
    t('byu-cbb', 'BYU', 'BYU Cougars', 'Big 12'),
    t('cincinnati-cbb', 'Cincinnati', 'Cincinnati Bearcats', 'Big 12'),
    t('houston-cbb', 'Houston', 'Houston Cougars', 'Big 12'),
    t('kansas-cbb', 'Kansas', 'Kansas Jayhawks', 'Big 12'),
    t('ksu-cbb', 'Kansas State', 'Kansas State Wildcats', 'Big 12'),
    t('oklahoma-st-cbb', 'Oklahoma State', 'Oklahoma State Cowboys', 'Big 12'),
    t('tcu-cbb', 'TCU', 'TCU Horned Frogs', 'Big 12'),
    t('texas-tech-cbb', 'Texas Tech', 'Texas Tech Red Raiders', 'Big 12'),
    t('ucf-cbb', 'UCF', 'UCF Knights', 'Big 12'),
    t('wvu-cbb', 'West Virginia', 'West Virginia Mountaineers', 'Big 12'),
    // Big Ten
    t('indiana-cbb', 'Indiana', 'Indiana Hoosiers', 'Big Ten'),
    t('iowa-cbb', 'Iowa', 'Iowa Hawkeyes', 'Big Ten'),
    t('maryland-cbb', 'Maryland', 'Maryland Terrapins', 'Big Ten'),
    t('michigan-cbb', 'Michigan', 'Michigan Wolverines', 'Big Ten'),
    t('mich-state-cbb', 'Michigan State', 'Michigan State Spartans', 'Big Ten'),
    t('minnesota-cbb', 'Minnesota', 'Minnesota Golden Gophers', 'Big Ten'),
    t('nebraska-cbb', 'Nebraska', 'Nebraska Cornhuskers', 'Big Ten'),
    t('northwestern-cbb', 'Northwestern', 'Northwestern Wildcats', 'Big Ten'),
    t('ohio-state-cbb', 'Ohio State', 'Ohio State Buckeyes', 'Big Ten'),
    t('penn-state-cbb', 'Penn State', 'Penn State Nittany Lions', 'Big Ten'),
    t('purdue-cbb', 'Purdue', 'Purdue Boilermakers', 'Big Ten'),
    t('rutgers-cbb', 'Rutgers', 'Rutgers Scarlet Knights', 'Big Ten'),
    // Pac-12 (remaining)
    t('oregon-state-cbb', 'Oregon State', 'Oregon State Beavers', 'Pac-12'),
    t('wsu-cbb', 'Washington State', 'Washington State Cougars', 'Pac-12'),
    // Mountain West
    t('fresno-state-cbb', 'Fresno State', 'Fresno State Bulldogs', 'Mountain West'),
    t('nevada-cbb', 'Nevada', 'Nevada Wolf Pack', 'Mountain West'),
    t('unlv-cbb', 'UNLV', 'UNLV Rebels', 'Mountain West'),
    t('new-mexico-cbb', 'New Mexico', 'New Mexico Lobos', 'Mountain West'),
    t('san-diego-state-cbb', 'San Diego State', 'San Diego State Aztecs', 'Mountain West'),
    t('san-jose-state-cbb', 'San Jose State', 'San Jose State Spartans', 'Mountain West'),
    t('air-force-cbb', 'Air Force', 'Air Force Falcons', 'Mountain West'),
    // AAC
    t('east-carolina-cbb', 'East Carolina', 'East Carolina Pirates', 'AAC'),
    t('memphis-cbb', 'Memphis', 'Memphis Tigers', 'AAC'),
    t('tulane-cbb', 'Tulane', 'Tulane Green Wave', 'AAC'),
    t('wichita-state-cbb', 'Wichita State', 'Wichita State Shockers', 'AAC'),
    // Sun Belt
    t('coastal-carolina-cbb', 'Coastal Carolina', 'Coastal Carolina Chanticleers', 'Sun Belt'),
    t('louisiana-cbb', 'Louisiana', 'Louisiana Ragin\' Cajuns', 'Sun Belt'),
    t('troy-cbb', 'Troy', 'Troy Trojans', 'Sun Belt'),
    // Conference USA
    t('la-tech-cbb', 'Louisiana Tech', 'Louisiana Tech Bulldogs', 'Conference USA'),
    t('old-dominion-cbb', 'Old Dominion', 'Old Dominion Monarchs', 'Conference USA'),
    // Colonial (CAA)
    t('elon-cbb', 'Elon', 'Elon Phoenix', 'CAA'),
    t('uncw-cbb', 'UNC Wilmington', 'UNC Wilmington Seahawks', 'CAA'),
    // Top independents / other notable programs
    t('oregon-cbb', 'Oregon', 'Oregon Ducks', 'Big Ten'),
    t('stanford-cbb', 'Stanford', 'Stanford Cardinal', 'ACC'),
    t('cal-cbb', 'California', 'California Golden Bears', 'ACC'),
    t('uconn-cbb', 'UConn', 'UConn Huskies', 'Big East'),
    t('creighton-cbb', 'Creighton', 'Creighton Bluejays', 'Big East'),
    t('seton-hall-cbb', 'Seton Hall', 'Seton Hall Pirates', 'Big East'),
    t('villanova-cbb', 'Villanova', 'Villanova Wildcats', 'Big East'),
  ],

  // ========== NHL ==========
  NHL: [
    // Atlantic
    t('bos-nhl', 'Boston', 'Boston Bruins', 'Atlantic'),
    t('buf', 'Buffalo', 'Buffalo Sabres', 'Atlantic'),
    t('det-nhl', 'Detroit', 'Detroit Red Wings', 'Atlantic'),
    t('fla-nhl', 'Florida', 'Florida Panthers', 'Atlantic'),
    t('mtl', 'Montreal', 'Montreal Canadiens', 'Atlantic'),
    t('ott', 'Ottawa', 'Ottawa Senators', 'Atlantic'),
    t('tb-nhl', 'Tampa Bay', 'Tampa Bay Lightning', 'Atlantic'),
    t('tor-nhl', 'Toronto', 'Toronto Maple Leafs', 'Atlantic'),
    // Metropolitan
    t('car', 'Carolina', 'Carolina Hurricanes', 'Metropolitan'),
    t('cbj', 'Columbus', 'Columbus Blue Jackets', 'Metropolitan'),
    t('nj', 'New Jersey', 'New Jersey Devils', 'Metropolitan'),
    t('nyi', 'NY Islanders', 'New York Islanders', 'Metropolitan'),
    t('nyr', 'NY Rangers', 'New York Rangers', 'Metropolitan'),
    t('phi-nhl', 'Philadelphia', 'Philadelphia Flyers', 'Metropolitan'),
    t('pit-nhl', 'Pittsburgh', 'Pittsburgh Penguins', 'Metropolitan'),
    t('wsh-nhl', 'Washington', 'Washington Capitals', 'Metropolitan'),
    // Central
    t('ari-nhl', 'Utah', 'Utah Hockey Club', 'Central'),
    t('chi-nhl', 'Chicago', 'Chicago Blackhawks', 'Central'),
    t('col-nhl', 'Colorado', 'Colorado Avalanche', 'Central'),
    t('dal-nhl', 'Dallas', 'Dallas Stars', 'Central'),
    t('min-nhl', 'Minnesota', 'Minnesota Wild', 'Central'),
    t('nsh', 'Nashville', 'Nashville Predators', 'Central'),
    t('stl-nhl', 'St. Louis', 'St. Louis Blues', 'Central'),
    t('wpg', 'Winnipeg', 'Winnipeg Jets', 'Central'),
    // Pacific
    t('ana', 'Anaheim', 'Anaheim Ducks', 'Pacific'),
    t('cgy', 'Calgary', 'Calgary Flames', 'Pacific'),
    t('edm', 'Edmonton', 'Edmonton Oilers', 'Pacific'),
    t('lak', 'Los Angeles', 'Los Angeles Kings', 'Pacific'),
    t('sjs', 'San Jose', 'San Jose Sharks', 'Pacific'),
    t('sea-nhl', 'Seattle', 'Seattle Kraken', 'Pacific'),
    t('van', 'Vancouver', 'Vancouver Canucks', 'Pacific'),
    t('vgk', 'Vegas', 'Vegas Golden Knights', 'Pacific'),
  ],

  // ========== NFL ==========
  PFB: [
    // AFC East
    t('buf-nfl', 'Buffalo', 'Buffalo Bills', 'AFC East'),
    t('mia-nfl', 'Miami', 'Miami Dolphins', 'AFC East'),
    t('ne', 'New England', 'New England Patriots', 'AFC East'),
    t('nyj', 'NY Jets', 'New York Jets', 'AFC East'),
    // AFC North
    t('bal-nfl', 'Baltimore', 'Baltimore Ravens', 'AFC North'),
    t('cin-nfl', 'Cincinnati', 'Cincinnati Bengals', 'AFC North'),
    t('cle-nfl', 'Cleveland', 'Cleveland Browns', 'AFC North'),
    t('pit-nfl', 'Pittsburgh', 'Pittsburgh Steelers', 'AFC North'),
    // AFC South
    t('hou-nfl', 'Houston', 'Houston Texans', 'AFC South'),
    t('ind-nfl', 'Indianapolis', 'Indianapolis Colts', 'AFC South'),
    t('jax', 'Jacksonville', 'Jacksonville Jaguars', 'AFC South'),
    t('ten', 'Tennessee', 'Tennessee Titans', 'AFC South'),
    // AFC West
    t('den-nfl', 'Denver', 'Denver Broncos', 'AFC West'),
    t('kc-nfl', 'Kansas City', 'Kansas City Chiefs', 'AFC West'),
    t('lv', 'Las Vegas', 'Las Vegas Raiders', 'AFC West'),
    t('lac-nfl', 'Los Angeles', 'Los Angeles Chargers', 'AFC West'),
    // NFC East
    t('dal-nfl', 'Dallas', 'Dallas Cowboys', 'NFC East'),
    t('nyg', 'NY Giants', 'New York Giants', 'NFC East'),
    t('phi-nfl', 'Philadelphia', 'Philadelphia Eagles', 'NFC East'),
    t('wsh-nfl', 'Washington', 'Washington Commanders', 'NFC East'),
    // NFC North
    t('chi-nfl', 'Chicago', 'Chicago Bears', 'NFC North'),
    t('det-nfl', 'Detroit', 'Detroit Lions', 'NFC North'),
    t('gb', 'Green Bay', 'Green Bay Packers', 'NFC North'),
    t('min-nfl', 'Minnesota', 'Minnesota Vikings', 'NFC North'),
    // NFC South
    t('atl-nfl', 'Atlanta', 'Atlanta Falcons', 'NFC South'),
    t('car-nfl', 'Carolina', 'Carolina Panthers', 'NFC South'),
    t('no', 'New Orleans', 'New Orleans Saints', 'NFC South'),
    t('tb-nfl', 'Tampa Bay', 'Tampa Bay Buccaneers', 'NFC South'),
    // NFC West
    t('ari-nfl', 'Arizona', 'Arizona Cardinals', 'NFC West'),
    t('lar', 'Los Angeles', 'Los Angeles Rams', 'NFC West'),
    t('sf-nfl', 'San Francisco', 'San Francisco 49ers', 'NFC West'),
    t('sea-nfl', 'Seattle', 'Seattle Seahawks', 'NFC West'),
  ],
};

// Legacy exports for backward compatibility
export const NATSTAT_BASKETBALL_LEAGUES = NATSTAT_SERVICES.Basketball;
export const BASEBALL_LEAGUES = NATSTAT_SERVICES.Baseball;
export const HOCKEY_LEAGUES = NATSTAT_SERVICES.Hockey;
export const FOOTBALL_LEAGUES = NATSTAT_SERVICES.Football;
export const SOCCER_LEAGUES = NATSTAT_SERVICES.Soccer;
export const GOLF_LEAGUES = NATSTAT_SERVICES.Golf;

// Get all services as flat array
export const getAllServices = () => {
  return Object.values(NATSTAT_SERVICES).flat();
};

// Farm Teams Configuration (for baseball)
export const FARM_TEAMS_CONFIG = {
  // MLB Team -> Farm System mapping
  teamHierarchy: {
    BAL: {
      name: 'Baltimore Orioles',
      affiliates: [
        { level: 'AAA', team: 'Norfolk Tides', code: 'NOR' },
        { level: 'AA', team: 'Bowie Baysox', code: 'BOW' },
        { level: 'A+', team: 'Aberdeen IronBirds', code: 'ABD' },
        { level: 'A', team: 'Delmarva Shorebirds', code: 'DEL' },
        { level: 'Rookie', team: 'FCL Orioles', code: 'FCL-BAL' },
      ],
    },
    NYY: {
      name: 'New York Yankees',
      affiliates: [
        { level: 'AAA', team: 'Scranton/Wilkes-Barre RailRiders', code: 'SWB' },
        { level: 'AA', team: 'Somerset Patriots', code: 'SOM' },
        { level: 'A+', team: 'Hudson Valley Renegades', code: 'HV' },
        { level: 'A', team: 'Tampa Tarpons', code: 'TAM' },
        { level: 'Rookie', team: 'FCL Yankees', code: 'FCL-NYY' },
      ],
    },
  },
  levelOrder: ['AAA', 'AA', 'A+', 'A', 'Rookie'],
  dataSource: 'natstat_milb',
};

/**
 * Get sport name from service code
 */
export const getSportFromCode = (serviceCode) => {
  for (const [sport, services] of Object.entries(NATSTAT_SERVICES)) {
    if (services.some(s => s.code === serviceCode)) {
      return sport;
    }
  }
  return null;
};

/**
 * Check if a service code belongs to a specific sport
 */
export const isServiceInSport = (serviceCode, sport) => {
  const services = NATSTAT_SERVICES[sport];
  return services ? services.some(s => s.code === serviceCode) : false;
};

// Legacy helper functions for backward compatibility
export const isNatStatBasketball = (sportCode) => isServiceInSport(sportCode, 'Basketball');
export const isBaseballLeague = (sportCode) => isServiceInSport(sportCode, 'Baseball');
export const isHockeyLeague = (sportCode) => isServiceInSport(sportCode, 'Hockey');
export const isFootballLeague = (sportCode) => isServiceInSport(sportCode, 'Football');
export const isSoccerLeague = (sportCode) => isServiceInSport(sportCode, 'Soccer');

/**
 * Get league info by code
 */
export const getLeagueInfo = (sportCode) => {
  for (const [sport, services] of Object.entries(NATSTAT_SERVICES)) {
    const service = services.find(s => s.code === sportCode);
    if (service) {
      return { ...service, sport: sport.toLowerCase() };
    }
  }
  return null;
};

/**
 * Fetch all available sports with service counts
 */
export const fetchNatStatSports = async () => {
  try {
    const response = await axios.get(
      `${NODE_BACKEND_URL}/api/natstat/sports`,
      { headers: { Authorization: getAuthToken() } }
    );

    const sportsResData = response.data?.data || response.data;
    if (response.data?.success && Array.isArray(sportsResData.sports)) {
      return sportsResData.sports;
    }
    // Fallback to local data
    return Object.entries(NATSTAT_SERVICES).map(([name, services]) => ({
      name,
      serviceCount: services.length,
    }));
  } catch (error) {
    console.warn('NatStat sports API unavailable, using fallback data');
    return Object.entries(NATSTAT_SERVICES).map(([name, services]) => ({
      name,
      serviceCount: services.length,
    }));
  }
};

/**
 * Fetch all available services/leagues from BigQuery
 * @param {string} sport - Optional sport filter (Basketball, Baseball, etc.)
 */
export const fetchNatStatServices = async (sport = null) => {
  try {
    let url = `${NODE_BACKEND_URL}/api/natstat/services`;
    if (sport) {
      url += `?sport=${encodeURIComponent(sport)}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: getAuthToken() }
    });

    const servicesResData = response.data?.data || response.data;
    if (response.data?.success && Array.isArray(servicesResData.services)) {
      return servicesResData.services;
    }
    // Fallback to local data
    return sport ? NATSTAT_SERVICES[sport] || [] : getAllServices();
  } catch (error) {
    console.warn('NatStat services API unavailable, using fallback data');
    return sport ? NATSTAT_SERVICES[sport] || [] : getAllServices();
  }
};

/**
 * Fetch teams from NatStat BigQuery for a specific league.
 * Falls back to hardcoded FALLBACK_TEAMS when backend is unavailable.
 */
export const fetchNatStatTeams = async (serviceCode) => {
  try {
    const response = await axios.get(
      `${NODE_BACKEND_URL}/api/natstat/teams/${serviceCode}`,
      { headers: { Authorization: getAuthToken() } }
    );

    const resData = response.data?.data || response.data;
    if (response.data?.success && Array.isArray(resData.teams) && resData.teams.length > 0) {
      return resData.teams.map(tm => ({
        team_id: tm.team_id || tm.id,
        team_name: tm.team_name || tm.name,
        full_name: tm.full_name || tm.team_name || tm.name,
        conference: tm.conference || tm.division || '',
        service_code: serviceCode,
      }));
    }
    // API returned success but empty — use fallback
    const fallback = FALLBACK_TEAMS[serviceCode];
    if (fallback) {
      return fallback.map(fb => ({ ...fb, service_code: serviceCode }));
    }
    return [];
  } catch (error) {
    console.warn(`NatStat API unavailable for ${serviceCode}, using fallback data`);
    const fallback = FALLBACK_TEAMS[serviceCode];
    if (fallback) {
      return fallback.map(fb => ({ ...fb, service_code: serviceCode }));
    }
    return [];
  }
};

/**
 * Fetch players from NatStat BigQuery for a specific league
 * @param {string} serviceCode - The service/league code (e.g., 'MBB', 'NBA')
 * @param {Object} options - Optional parameters
 * @param {number} options.limit - Max number of players to return (default: 100)
 * @param {string} options.teamId - Filter by team code/ID
 */
export const fetchNatStatPlayers = async (serviceCode, options = {}) => {
  try {
    const { limit = 100, teamId } = typeof options === 'number'
      ? { limit: options } // Backward compatibility: if number passed, treat as limit
      : options;

    let url = `${NODE_BACKEND_URL}/api/natstat/players/${serviceCode}?limit=${limit}`;
    if (teamId) {
      url += `&teamId=${encodeURIComponent(teamId)}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: getAuthToken() }
    });

    const playerResData = response.data?.data || response.data;
    if (response.data?.success && Array.isArray(playerResData.players)) {
      return playerResData.players;
    }
    return [];
  } catch (error) {
    console.warn(`NatStat players API unavailable for ${serviceCode}`);
    return [];
  }
};

/**
 * Fetch roster (players) for a specific team
 * Convenience function that wraps fetchNatStatPlayers with team filter
 * @param {string} serviceCode - The service/league code
 * @param {string} teamId - The team ID to fetch roster for
 */
export const fetchTeamRoster = async (serviceCode, teamId) => {
  return fetchNatStatPlayers(serviceCode, { teamId, limit: 200 });
};

/**
 * Fetch games from NatStat BigQuery for a specific league
 */
export const fetchNatStatGames = async (serviceCode, options = {}) => {
  try {
    const { season, teamId, limit = 50 } = options;
    let url = `${NODE_BACKEND_URL}/api/natstat/games/${serviceCode}?limit=${limit}`;
    if (season) url += `&season=${season}`;
    if (teamId) url += `&teamId=${teamId}`;

    const response = await axios.get(url, {
      headers: { Authorization: getAuthToken() }
    });

    const gamesResData = response.data?.data || response.data;
    if (response.data?.success && Array.isArray(gamesResData.games)) {
      return gamesResData.games;
    }
    return [];
  } catch (error) {
    console.warn(`NatStat games API unavailable for ${serviceCode}`);
    return [];
  }
};

/**
 * Fetch summary statistics from NatStat
 * @param {string} sport - Optional sport filter
 */
export const fetchNatStatStats = async (sport = null) => {
  try {
    let url = `${NODE_BACKEND_URL}/api/natstat/stats`;
    if (sport) {
      url += `?sport=${encodeURIComponent(sport)}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: getAuthToken() }
    });

    const statsResData = response.data?.data || response.data;
    if (response.data?.success && statsResData.stats) {
      return statsResData.stats;
    }
    return null;
  } catch (error) {
    console.warn('NatStat stats API unavailable');
    return null;
  }
};

/**
 * Get farm team affiliates for an MLB team
 */
export const getFarmTeamAffiliates = (mlbTeamCode) => {
  const teamData = FARM_TEAMS_CONFIG.teamHierarchy[mlbTeamCode];
  if (!teamData) return [];

  return teamData.affiliates.map(affiliate => ({
    team_id: affiliate.code.toLowerCase(),
    team_name: affiliate.team,
    full_name: affiliate.team,
    conference: affiliate.level,
    parent_team: mlbTeamCode,
    level: affiliate.level,
  }));
};

/**
 * Get all farm teams across the system
 */
export const getAllFarmTeams = () => {
  const allFarmTeams = [];

  Object.entries(FARM_TEAMS_CONFIG.teamHierarchy).forEach(([mlbCode, teamData]) => {
    teamData.affiliates.forEach(affiliate => {
      allFarmTeams.push({
        team_id: affiliate.code.toLowerCase(),
        team_name: affiliate.team,
        full_name: `${affiliate.team} (${teamData.name})`,
        conference: affiliate.level,
        parent_team: mlbCode,
        parent_name: teamData.name,
        level: affiliate.level,
      });
    });
  });

  return allFarmTeams.sort((a, b) => {
    const levelA = FARM_TEAMS_CONFIG.levelOrder.indexOf(a.level);
    const levelB = FARM_TEAMS_CONFIG.levelOrder.indexOf(b.level);
    if (levelA !== levelB) return levelA - levelB;
    return a.team_name.localeCompare(b.team_name);
  });
};

export default {
  NATSTAT_SERVICES,
  FALLBACK_TEAMS,
  NATSTAT_BASKETBALL_LEAGUES,
  BASEBALL_LEAGUES,
  HOCKEY_LEAGUES,
  FOOTBALL_LEAGUES,
  SOCCER_LEAGUES,
  FARM_TEAMS_CONFIG,
  getAllServices,
  getSportFromCode,
  isServiceInSport,
  isNatStatBasketball,
  isBaseballLeague,
  isHockeyLeague,
  isFootballLeague,
  isSoccerLeague,
  getLeagueInfo,
  fetchNatStatSports,
  fetchNatStatServices,
  fetchNatStatTeams,
  fetchNatStatPlayers,
  fetchTeamRoster,
  fetchNatStatGames,
  fetchNatStatStats,
  getFarmTeamAffiliates,
  getAllFarmTeams,
};
